/**
 * Core patient field catalog — single source of truth for the patient form.
 *
 * Each entry maps a stable `field_key` to a DB column (when applicable),
 * a UI step, and default metadata (label, required, visible, order, type).
 *
 * The Patient Form Settings page renders these defaults and merges any
 * DB-level overrides on top (Phase 1: global overrides via patient_field_definitions).
 */

// ─── Types ───

export type PatientFormStep =
  | 'Personal Info'
  | 'Contact'
  | 'Emergency Contact'
  | 'Medication'
  | 'Intake Form';

export type PatientFieldType =
  | 'text'
  | 'select'
  | 'date'
  | 'email'
  | 'tel'
  | 'textarea'
  | 'checkbox';

export interface PatientCoreFieldDef {
  /** Stable identifier used for keying, DB override matching, etc. */
  field_key: string;
  /** Column on `patients` or related table. Undefined for external/UI-only fields. */
  patientsColumn?: string;
  /** Related table if not on `patients` directly */
  table?: string;
  /** UI step grouping */
  step: PatientFormStep;
  /** Default display label */
  label: string;
  /** Whether the field is required by default */
  required: boolean;
  /** Whether the field is visible by default */
  visible: boolean;
  /** Display order (0-based within step) */
  order: number;
  /** Input type hint */
  type: PatientFieldType;
  /** If true, field cannot be toggled off (e.g. patientId, patientName) */
  locked?: boolean;
  /** System note shown in the Notes column */
  notes?: string;
}

/** Union of all known field keys */
export type PatientCoreFieldKey = (typeof PATIENT_CORE_FIELDS)[number]['field_key'];

// ─── Catalog ───

let order = 0;

export const PATIENT_CORE_FIELDS: readonly PatientCoreFieldDef[] = [
  // ─── Personal Info ───
  {
    field_key: 'location',
    patientsColumn: undefined,
    step: 'Personal Info',
    label: 'Location',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'doctor',
    patientsColumn: 'assigned_doctor',
    step: 'Personal Info',
    label: 'Doctor',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'patientId',
    patientsColumn: 'patient_number',
    step: 'Personal Info',
    label: 'Patient Number',
    required: true,
    visible: true,
    order: order++,
    type: 'text',
    locked: true,
    notes: 'System-managed field',
  },
  {
    field_key: 'title',
    patientsColumn: 'title',
    step: 'Personal Info',
    label: 'Title',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'patientName',
    patientsColumn: 'full_name',
    step: 'Personal Info',
    label: 'Patient Name',
    required: true,
    visible: true,
    order: order++,
    type: 'text',
    locked: true,
    notes: 'System-managed field',
  },
  {
    field_key: 'idType',
    table: 'patient_identifiers',
    patientsColumn: 'id_type',
    step: 'Personal Info',
    label: 'ID Type',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'idNumber',
    table: 'patient_identifiers',
    patientsColumn: 'id_value',
    step: 'Personal Info',
    label: 'ID Number',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'localName',
    patientsColumn: 'local_name',
    step: 'Personal Info',
    label: 'Local Name',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'countryOfBirth',
    patientsColumn: 'country_of_birth',
    step: 'Personal Info',
    label: 'Country of Birth',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'gender',
    patientsColumn: 'sex',
    step: 'Personal Info',
    label: 'Gender',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'maritalStatus',
    patientsColumn: 'marital_status',
    step: 'Personal Info',
    label: 'Marital Status',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'dateOfBirth',
    patientsColumn: 'birth_date',
    step: 'Personal Info',
    label: 'Date of Birth',
    required: false,
    visible: true,
    order: order++,
    type: 'date',
  },
  {
    field_key: 'shift',
    patientsColumn: 'shift',
    step: 'Personal Info',
    label: 'Shift',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'nationality',
    patientsColumn: 'nationality',
    step: 'Personal Info',
    label: 'Nationality',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'race',
    patientsColumn: 'race',
    step: 'Personal Info',
    label: 'Race',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'literacyLevel',
    patientsColumn: 'literacy_level',
    step: 'Personal Info',
    label: 'Literacy Level',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'religion',
    patientsColumn: 'religion',
    step: 'Personal Info',
    label: 'Religion',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'occupation',
    patientsColumn: 'occupation',
    step: 'Personal Info',
    label: 'Occupation',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'referredBy',
    patientsColumn: 'referred_by',
    step: 'Personal Info',
    label: 'Referred By',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'departmentName',
    patientsColumn: 'department_name',
    step: 'Personal Info',
    label: 'Department Name',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'jobPosition',
    patientsColumn: 'job_position',
    step: 'Personal Info',
    label: 'Job Position',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'status',
    patientsColumn: 'status',
    step: 'Personal Info',
    label: 'Status',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },

  // ─── Contact ───
  {
    field_key: 'email',
    table: 'patient_contacts',
    patientsColumn: 'email',
    step: 'Contact',
    label: 'Email Address',
    required: false,
    visible: true,
    order: order++,
    type: 'email',
  },
  {
    field_key: 'mobileCountryCode',
    table: 'patient_contacts',
    patientsColumn: 'mobile_country_code',
    step: 'Contact',
    label: 'Mobile Country Code',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'mobileNumber',
    table: 'patient_contacts',
    patientsColumn: 'mobile_number',
    step: 'Contact',
    label: 'Mobile Number',
    required: false,
    visible: true,
    order: order++,
    type: 'tel',
  },
  {
    field_key: 'telephone',
    table: 'patient_contacts',
    patientsColumn: 'formal_telephone',
    step: 'Contact',
    label: 'Telephone',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'postalCode',
    table: 'patient_contacts',
    patientsColumn: 'formal_postal_code',
    step: 'Contact',
    label: 'Postal Code',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'unit',
    table: 'patient_contacts',
    patientsColumn: 'formal_unit',
    step: 'Contact',
    label: 'Unit (#Floor-Unit No)',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'residentialAddress',
    table: 'patient_contacts',
    patientsColumn: 'formal_residential_address',
    step: 'Contact',
    label: 'Residential Address',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'residentialAddressLocal',
    table: 'patient_contacts',
    patientsColumn: 'formal_residential_address_local',
    step: 'Contact',
    label: 'Residential Address \u2013 Local',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'provinceState',
    table: 'patient_contacts',
    patientsColumn: 'formal_province_state',
    step: 'Contact',
    label: 'Province/State',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'district',
    table: 'patient_contacts',
    patientsColumn: 'formal_district',
    step: 'Contact',
    label: 'District',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'subDistrict',
    table: 'patient_contacts',
    patientsColumn: 'formal_sub_district',
    step: 'Contact',
    label: 'Sub-District',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'officeTelephone',
    table: 'patient_contacts',
    patientsColumn: 'office_telephone',
    step: 'Contact',
    label: 'Office Telephone No.',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'officeAddress',
    table: 'patient_contacts',
    patientsColumn: 'office_address',
    step: 'Contact',
    label: 'Office Address',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },

  // ─── Emergency Contact ───
  {
    field_key: 'emergencyContactName',
    table: 'patient_emergency_contacts',
    patientsColumn: 'name',
    step: 'Emergency Contact',
    label: 'Next of Kin Name',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'emergencyContactRelationship',
    table: 'patient_emergency_contacts',
    patientsColumn: 'relationship',
    step: 'Emergency Contact',
    label: 'Relationship',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'emergencyContactEmail',
    table: 'patient_emergency_contacts',
    patientsColumn: 'email',
    step: 'Emergency Contact',
    label: 'Next of Kin Email',
    required: false,
    visible: true,
    order: order++,
    type: 'email',
  },
  {
    field_key: 'emergencyContactPhone',
    table: 'patient_emergency_contacts',
    patientsColumn: 'phone',
    step: 'Emergency Contact',
    label: 'Next of Kin Phone',
    required: false,
    visible: true,
    order: order++,
    type: 'tel',
  },

  // ─── Medication ───
  {
    field_key: 'drugAllergies',
    patientsColumn: 'drug_allergies_notes',
    step: 'Medication',
    label: 'Drug Allergies',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'drugAllergiesSpecify',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Drug Allergies - Specify',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'drugAllergySymptoms',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Drug Allergy Symptoms',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'drugAllergySeverity',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Drug Allergy Severity',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'foodAllergies',
    patientsColumn: 'food_allergies_notes',
    step: 'Medication',
    label: 'Food Allergies',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'foodAllergiesSpecify',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Food Allergies - Specify',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'foodAllergySymptoms',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Food Allergy Symptoms',
    required: false,
    visible: true,
    order: order++,
    type: 'text',
  },
  {
    field_key: 'foodAllergySeverity',
    patientsColumn: undefined,
    step: 'Medication',
    label: 'Food Allergy Severity',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'aboBloodGroup',
    patientsColumn: 'blood_group_abo',
    step: 'Medication',
    label: 'ABO Blood Group',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'rhAntigen',
    patientsColumn: 'blood_group_rh',
    step: 'Medication',
    label: 'Rh Antigen',
    required: false,
    visible: true,
    order: order++,
    type: 'select',
  },
  {
    field_key: 'notes',
    patientsColumn: 'medical_notes',
    step: 'Medication',
    label: 'Notes',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'alerts',
    patientsColumn: 'medical_alerts',
    step: 'Medication',
    label: 'Alerts',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'pastMedication',
    patientsColumn: 'past_medication_notes',
    step: 'Medication',
    label: 'Past Medication',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'currentMedication',
    patientsColumn: 'current_medication_notes',
    step: 'Medication',
    label: 'Current Medication',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
  {
    field_key: 'medicalHistory',
    patientsColumn: 'medical_history',
    step: 'Medication',
    label: 'Medical History',
    required: false,
    visible: true,
    order: order++,
    type: 'textarea',
  },
] as const satisfies readonly PatientCoreFieldDef[];

/** All distinct steps in catalog order */
export const PATIENT_FORM_STEPS: readonly PatientFormStep[] = [
  'Personal Info',
  'Contact',
  'Emergency Contact',
  'Medication',
  'Intake Form',
];
